import { IAdminCustomerReferralsResponse, ICustomer } from "../../models/ICustomers"
import { IPaymentRecipt } from "../../models/IPayments"
import { IPoint, IRewardRecipt} from "../../models/IRewards"
import DB_COLLECTIONS from "../../utils/database"
import { currency } from "../../utils/utils"
import BarsService from "../bar/bar.service"
import ApiService from "../api/api.service"
import { DatabaseService } from "../database/database.service"

interface IManualPointsGrantResponse {
    userId: string;
    barId: string;
    requested: number;
    created: number;
    cappedByPendingLimit: boolean;
}

const getCustomers = async () => {
    return DatabaseService.getCol<ICustomer>(DB_COLLECTIONS.customers())
    .then((customers) => customers.filter((c) => !!c.stripeId))
}

const getCustomer = async (id: string) => {
    return DatabaseService.getDoc<ICustomer>(DB_COLLECTIONS.customer(id))
}

const deleteCustomer = async (id: string) => {
    return DatabaseService.remove(DB_COLLECTIONS.customer(id));
}

const searchCustomer = async (str: string) => {
    return DatabaseService.getCol<ICustomer>(DB_COLLECTIONS.customers(), ref => ref.where("search", 'array-contains', str.toLowerCase()))
}

const getPoints = (userId: string) => {
    return DatabaseService.getCol<IPoint>(
        DB_COLLECTIONS.customer(userId) + "/" + DB_COLLECTIONS.points()
    )
}

const getRewards = (userId: string) => {
    return DatabaseService.getCol<IRewardRecipt>(DB_COLLECTIONS.rewards(), (ref) => ref.where("userId", '==', userId))
}

const getPaymentRecipts = (userId: string) => {
    return DatabaseService.getCol<IPaymentRecipt>(DB_COLLECTIONS.payment_recipts(), (ref) => ref.where("customerId", '==', userId))
}

const getReports = async (userId: string) => {

    const points = await getPoints(userId);
    const rewards = await getRewards(userId);
    const recipts = await getPaymentRecipts(userId);

    const moneySpent = recipts.reduce((acc, cur) => acc + currency(cur.amount).toUnit(), 0);
    const pointsEarned = points.reduce((acc) => acc + 1, 0);
    const rewardsRedeemed = rewards.length;

    const barIds = Array.from(new Set(recipts.map(({ barId }) => barId)));

    const barsData = await Promise.all(barIds.map(async (id) => {

        const { name } = await BarsService.getBar(id);
        const numberOfRewards = rewards.filter((r) => r.barId === id).length;
        const amountSpent = recipts.filter((r) => r.barId === id).reduce((acc, cur) => acc + currency(cur.amount).toUnit(), 0);

        return {
            name,
            numberOfRewards,
            amountSpent
        }
    }));

    return {
        moneySpent,
        pointsEarned,
        rewardsRedeemed,
        barsData
    }
}

const addManualPoints = async (
    userId: string,
    points: number,
    barId: string = "manual"
) => {
    const normalizedBarId = (barId || "").trim() || "manual";
    return ApiService.postJson<IManualPointsGrantResponse>(`/customers/${encodeURIComponent(userId)}/points`, {
        barId: normalizedBarId,
        points
    });
}

const getAdminCustomerReferrals = async (userId: string) => {
    return ApiService.getJson<IAdminCustomerReferralsResponse>(
        `/admin/customers/${encodeURIComponent(userId)}/referrals`
    );
}



const CustomerService = {
    getCustomers,
    getCustomer,
    deleteCustomer,
    searchCustomer,
    getReports,
    addManualPoints,
    getAdminCustomerReferrals
}

export default CustomerService
